import type { Extension } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import { ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionKeyDownProps } from '@tiptap/suggestion';
import { colors } from 'theme/colors';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';

import { SuggestionList } from 'components/comments/SuggestionList';

import type { User } from 'types/data/user';

interface EditorConfigProps {
  placeholder?: string;
  enableMentions?: boolean;
  users?: User[];
  isUsersLoading?: boolean;
}

type MentionUser = { id: string; name: string };

export const getEditorExtensions = (options: EditorConfigProps = {}) => {
  const {
    placeholder = 'Write something...',
    enableMentions = false,
    users = [],
    isUsersLoading = false,
  } = options;

  const extensions = [
    StarterKit,
    Placeholder.configure({ placeholder }),
    Highlight.configure({
      HTMLAttributes: { style: `background-color: ${colors.lime2};` },
    }),
    Underline,
    Strike,
  ];

  if (enableMentions) {
    const mention = Mention.configure({
      HTMLAttributes: {
        class: 'mention rounded px-1',
        style: `background-color: ${colors.cyan1}; color: ${colors.cyan7};`,
      },
      deleteTriggerWithBackspace: true,
      suggestion: {
        char: '@',
        items: ({ query }: { query: string }) =>
          users.filter((u) =>
            u.name.toLowerCase().includes((query ?? '').toLowerCase())
          ),
        render: () => {
          let tip: TippyInstance | null = null;
          let reactRenderer: ReactRenderer | null = null;
          let currentItems: MentionUser[] = [];
          let executeCommand: (item: {
            id: string;
            label: string;
          }) => void = () => {};
          let activeIndex = 0;

          return {
            onStart: ({ items, command, clientRect, editor }) => {
              currentItems = items as MentionUser[];
              executeCommand = command as any;
              activeIndex = 0;
              reactRenderer = new ReactRenderer(SuggestionList as any, {
                editor,
                props: {
                  items: items as MentionUser[],
                  command,
                  activeIndex,
                  isLoading: isUsersLoading,
                },
              });

              tip = tippy(document.body, {
                getReferenceClientRect: (clientRect as any) ?? undefined,
                content: reactRenderer.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'top-start',
                popperOptions: {
                  modifiers: [{ name: 'flip', enabled: false }],
                },
                appendTo: () => document.body,
              });
            },
            onUpdate: ({ items, command, clientRect }) => {
              if (!tip || !reactRenderer) return;
              tip.setProps({
                getReferenceClientRect: (clientRect as any) ?? undefined,
              });
              activeIndex = Math.min(
                activeIndex,
                Math.max(items.length - 1, 0)
              );
              reactRenderer.updateProps({
                items: items as MentionUser[],
                command,
                activeIndex,
                isLoading: isUsersLoading,
              });
              currentItems = items as MentionUser[];
              executeCommand = command as any;
            },
            onKeyDown: (props: SuggestionKeyDownProps) => {
              const { event } = props;

              if (event.key === 'Escape') {
                tip?.destroy();
                reactRenderer?.destroy();
                return true;
              }

              if (event.key === 'Enter') {
                const selected = currentItems[activeIndex] ?? currentItems[0];
                if (selected) {
                  executeCommand({ id: selected.id, label: selected.name });
                  return true;
                }
              }

              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                if (currentItems.length === 0) return false;

                const direction = event.key === 'ArrowDown' ? 1 : -1;
                activeIndex =
                  (activeIndex + direction + currentItems.length) %
                  currentItems.length;

                reactRenderer?.updateProps({
                  items: currentItems,
                  command: executeCommand,
                  activeIndex,
                  isLoading: isUsersLoading,
                });
                return true;
              }

              return false;
            },
            onExit: () => {
              tip?.destroy();
              reactRenderer?.destroy();
            },
          };
        },
      },
    }) as Extension;

    extensions.push(mention);
  }

  return extensions as Extension[];
};

export const getDisplayEditorExtensions = () => [
  StarterKit,
  Highlight.configure({
    HTMLAttributes: {
      style: `background-color: ${colors.lime2};`,
    },
  }),
  Underline,
  Strike,
  Mention.configure({
    HTMLAttributes: {
      class: 'mention rounded px-1',
      style: `background-color: ${colors.cyan1}; color: ${colors.cyan7};`,
    },
  }),
];
